
import { useEffect, useCallback, useReducer, useRef, useState } from "react";
import { Model } from "../models/model";
import { fetchConToken } from "../helpers/fetch";
import { toast } from "react-toastify";
import { moveDealBetweenStages, movementNeighbors, applyMovement } from "../helpers/crmBoard";
import { createCrmMoveQueue } from "../helpers/crmMoveQueue";

const getEmptyLeadForm = () => ({
    id: null,
    id_contacto: null,
    referencia: null,
    //id_etapa: funnels[0]?.id || null,
    id_etapa: null,
    email: null,
    fono: null,
    total: 0,
    // id_moneda: local_id_moneda,
    id_moneda: null,
    nota: null,
    id_tipo_prospecto: null,
    ubicacion: null,
    id_ppto: null,
    nro_ppto: null,
    id_origen: null,
    estado: null,
    referido_por: null,
});

const initialState = {
    leads: [],
    funnels: [],

    selectedLead: null,
    funnel_defauld_id: null,

    leadForm: getEmptyLeadForm(),

    catalogs: {
        tiposProspectos: [],
        clasificaciones: [],
        origenes: [],
    },

    filters: {
        id_tipo: "",
        estado: 'activo',
        estado_comercial: 'abierta',
        text: "",
        page: 1,
        rows: 10
    },

    optionView: 'board',
    openDialog: false,
    loading: false,
    error: null,
};

function reducerActions(state, action) {
    switch (action.type) {
        case "SET_LEADS":
            return {
                ...state,
                leads: action.payload,
            };

        case "SET_FUNNELS":
            return {
                ...state,
                funnels: action.payload,
            };

        case "SET_FUNNEL_DEFAULT_ID":
            return {
                ...state,
                funnel_defauld_id: action.payload,
            };

        case "SET_FORM":
            return {
                ...state,
                leadForm: {
                    ...state.leadForm,
                    ...action.payload,
                },
            };

        case "RESET_FORM":
            return {
                ...state,
                leadForm: initialState.leadForm,
            };

        case "OPEN_EDIT_LEAD":
            return {
                ...state,

                leadForm: {
                    ...action.payload
                },

                openDialog: true
            };

        case "OPEN_CREATE_LEAD":
            return {
                ...state,
                leadForm: {
                    ...getEmptyLeadForm(),
                    id_etapa: action.payload
                },
                openDialog: true
            };

        case "SET_FILTER":
            return {
                ...state,
                filters: {
                    ...state.filters,
                    [action.payload.name]: action.payload.value
                },
            };

        case "SET_OPEN_DIALOG":
            return {
                ...state,
                openDialog: action.payload
            };

        case "SET_LOADING":
            return {
                ...state,
                loading: action.payload
            };

        case "SET_OPTION_VIEW":
            return {
                ...state,
                optionView: action.payload,
            };

        case "SET_CATALOGS":
            return {
                ...state,
                catalogs: action.payload,
            };

        default:
            return state;
    }
}

export function useCrm() {
    const [dataRevision, refreshIndicators] = useReducer(value => value + 1, 0);
    const [savingLeads, setSavingLeads] = useState({});
    const movementOverlays = useRef(new Map());
    const boardRef = useRef([]);
    const queueRef = useRef(null);
    const leadsRequest = useRef(0);

    const [state, dispatch] = useReducer(reducerActions, initialState);
    const { leads, funnels, funnel_defauld_id, leadForm, catalogs, filters, optionView, openDialog, loading } = state
    const viewKey = JSON.stringify([funnel_defauld_id, filters]);
    const currentView = useRef(viewKey);
    currentView.current = viewKey;
    boardRef.current = leads;
    const publishBoard = board => {
        boardRef.current = board;
        dispatch({ type: 'SET_LEADS', payload: board });
    };
    if (!queueRef.current) {
        queueRef.current = createCrmMoveQueue({
            send: async (id, payload) => {
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 15000);
                try {
                    const response = await fetchConToken('crm/' + id + '/move', payload, 'PATCH', { signal: controller.signal });
                    const body = await response.json();
                    if (!response.ok || !body.ok) {
                        const error = new Error(body.msg || 'No se pudo guardar el movimiento');
                        error.conflict = response.status === 409;
                        throw error;
                    }
                    if (!Number.isInteger(body.data?.crm_version)) throw new Error('Respuesta de movimiento inválida');
                    return body.data;
                } catch (error) {
                    if (error.name === 'AbortError') throw new Error('El servidor tardó demasiado. Reintenta el guardado.');
                    throw error;
                } finally {
                    clearTimeout(timeout);
                }
            },
            onChange: setSavingLeads,
            onSaved: (id, data, latest) => {
                if (latest) refreshIndicators();
                const overlay = movementOverlays.current.get(String(id));
                if (overlay && latest) overlay.confirmedVersion = data.crm_version;
                // Acknowledge metadata without undoing a newer drop of the same card.
                publishBoard(boardRef.current.map(stage => ({ ...stage, leads: stage.leads.map(lead =>
                    String(lead.id) === String(id) ? { ...lead, crm_version: data.crm_version, crm_posicion: data.crm_posicion } : lead) })));
            },
        });
    }

    const getFunnels = async () => {
        const resultsFunnels = await Model.getAll({ url: "crm/funnels/all" });
        if (!Array.isArray(resultsFunnels)) return;
        const foundFunnel = resultsFunnels.find((it) => it.por_defecto === true) || resultsFunnels[0];

        if (foundFunnel) {
            dispatch({
                type: "SET_FUNNEL_DEFAULT_ID",
                payload: foundFunnel.id,
            });
        }

        dispatch({
            type: "SET_FUNNELS",
            payload: resultsFunnels,
        });

    };

    const getLeads = useCallback(async () => {
        const requestId = ++leadsRequest.current;
        try {

            const params = new URLSearchParams(filters).toString();
            const results = await Model.getAll({
                url: `crm/${funnel_defauld_id}/?${params}`,
            });

            if (requestId !== leadsRequest.current || currentView.current !== JSON.stringify([funnel_defauld_id, filters])) return;
            if (!Array.isArray(results)) return;
            let reconciled = results;
            for (const [id, overlay] of movementOverlays.current) {
                const serverLead = results.flatMap(stage => stage.leads).find(lead => String(lead.id) === id);
                if (overlay.confirmedVersion != null && serverLead && Number(serverLead.crm_version) >= overlay.confirmedVersion) {
                    movementOverlays.current.delete(id);
                } else reconciled = applyMovement(reconciled, id, overlay.intent);
            }
            boardRef.current = reconciled;
            dispatch({ type: "SET_LEADS", payload: reconciled });
            return results;

        } catch (error) {

            dispatch({
                type: "SET_ERROR",
                payload: error.message,
            });

        }
    }, [filters, funnel_defauld_id]);

    const loadCatalogs = async () => {
        const [
            tipos,
            clasificaciones,
            origenes,
        ] = await Promise.all([
            Model.getAll({ url: "crm/tipos/all" }),
            Model.getAll({ url: "crm/clas/all" }),
            Model.getAll({ url: "crm/origen/all" }),
        ]);

        dispatch({
            type: "SET_CATALOGS",
            payload: {
                tiposProspectos: tipos,
                clasificaciones,
                origenes,
            },
        });
    };

    const setOpenDialog = (open) => {
        if (!open) refreshIndicators();
        dispatch({
            type: "SET_OPEN_DIALOG",
            payload: open
        });
    };

    const setOptionView = (view) => {
        dispatch({
            type: "SET_OPTION_VIEW",
            payload: view,
        });
    };

    const openEditLead = (lead) => {
        dispatch({
            type: "OPEN_EDIT_LEAD",
            payload: lead,
        });
    };

    const openCreateLead = () => {
        dispatch({
            type: "OPEN_CREATE_LEAD",
            payload: leads.find(stage => !['ganado', 'perdido'].includes(stage.tipo))?.id || null
        });
    };

    const updateForm = (payload) => {
        dispatch({
            type: "SET_FORM",
            payload,
        });
    };

    const handleEditById = async (opId, etapaId) => {
        const etapaFound = leads.find((it) => it.id === etapaId);
        if (etapaFound) {
            const leadFound = etapaFound.leads.find((op) => op.id === opId);
            if (leadFound) {
                updateForm(leadFound)
                setOpenDialog(true)
                return;
            }
        }
        try {
            const response = await fetchConToken(`crm/deal/${opId}`);
            const body = await response.json();
            if (!response.ok || !body.ok) throw new Error(body.msg || 'No se pudo abrir la oportunidad');
            openEditLead(body.data);
        } catch (error) { toast.error(error.message); }
    };

    const handleFilters = useCallback(({ target }) => {
        dispatch({
            type: "SET_FILTER",
            payload: {
                name: target.name,
                value: target.value,
            },
        });
    }, []);

    const saveLead = async (lead = state.leadForm) => {
        try {

            dispatch({
                type: "SET_LOADING",
                payload: true
            });

            console.log({ lead })

            if (lead.id) {

                await Model.update({
                    url: `crm/${lead.id}`,
                    dataForm: lead,
                    resultData: true,
                });

            } else {

                await Model.save({
                    url: "crm",
                    dataForm: lead,
                    resultData: true,
                });

                dispatch({
                    type: "RESET_FORM",
                });

            }

            dispatch({
                type: "SET_LOADING",
                payload: false
            });


            await getLeads();
            refreshIndicators();

        } catch (error) {
            console.error(error);
        }
    };

    const moveLead = (lead, destinationStageId, destinationIndex = 0) => {
        if (lead.estado_comercial && lead.estado_comercial !== 'abierta') return;
        const board = boardRef.current;
        const source = board.find(stage => stage.leads.some(item => String(item.id) === String(lead.id)));
        const actualLead = source?.leads.find(item => String(item.id) === String(lead.id));
        if (!actualLead) return;
        const updated = moveDealBetweenStages(board, actualLead.id, destinationStageId, destinationIndex);
        if (updated === board) return;
        const intent = movementNeighbors(updated, actualLead.id, destinationStageId);
        movementOverlays.current.set(String(actualLead.id), { intent, confirmedVersion: null });
        publishBoard(updated);
        queueRef.current.enqueue(actualLead.id, intent, actualLead.crm_version);
    };

    const reorderLead = (leadId, targetId, stageId) => {
        const stage = boardRef.current.find(stage => String(stage.id) === String(stageId));
        if (!stage) return;
        const from = stage.leads.findIndex(lead => String(lead.id) === String(leadId));
        const to = stage.leads.findIndex(lead => String(lead.id) === String(targetId));
        if (from < 0 || to < 0 || from === to) return;
        moveLead(stage.leads[from], stage.id, to);
    };

    const retryMove = async id => {
        if (savingLeads[id]?.conflict) {
            const results = await getLeads();
            const lead = results?.flatMap(stage => stage.leads).find(item => String(item.id) === String(id));
            if (!lead) {
                toast.error('No se pudo actualizar la oportunidad. Intenta recargar el tablero.');
                return;
            }
            queueRef.current.retry(id, lead.crm_version);
        } else queueRef.current.retry(id);
    };

    useEffect(() => {
        const warnUnsaved = event => {
            if (queueRef.current.hasPending()) { event.preventDefault(); event.returnValue = ''; }
        };
        window.addEventListener('beforeunload', warnUnsaved);
        return () => window.removeEventListener('beforeunload', warnUnsaved);
    }, []);

    useEffect(() => {
        getFunnels();
    }, []);

    useEffect(() => {
        loadCatalogs();
    }, []);

    useEffect(() => {
        if (funnel_defauld_id > 0) getLeads();
    }, [funnel_defauld_id, getLeads]);

    return {
        reloadLeads: getLeads,
        applyCommercialResult: result => {
            ++leadsRequest.current;
            movementOverlays.current.delete(String(result.id));
            let updated = boardRef.current;
            const source = updated.find(stage => stage.leads.some(lead => String(lead.id) === String(result.id)));
            if (source && String(source.id) !== String(result.id_etapa)) updated = moveDealBetweenStages(updated, result.id, result.id_etapa, 0);
            publishBoard(updated.map(stage => {
                const removed = stage.leads.find(lead => String(lead.id) === String(result.id) && filters.estado_comercial && filters.estado_comercial !== result.estado_comercial);
                return { ...stage, leads: stage.leads.filter(lead => lead !== removed).map(lead => String(lead.id) === String(result.id) ? { ...lead, ...result } : lead),
                    cant_deals: Number(stage.cant_deals || 0) - (removed ? 1 : 0), suma_ingreso: Number(stage.suma_ingreso || 0) - (Number(removed?.total) || 0) };
            }));
            if (String(leadForm.id) === String(result.id)) updateForm(result);
            refreshIndicators();
        },
        dataRevision,
        refreshIndicators,
        funnelId: funnel_defauld_id,
        selectFunnel: id => {
            const funnel = funnels.find(item => String(item.id) === String(id));
            if (funnel) dispatch({ type: 'SET_FUNNEL_DEFAULT_ID', payload: funnel.id });
        },
        reorderLead,
        moveLead,
        savingLeads,
        retryMove,
        leads,
        funnels,

        optionView,
        setOptionView,

        loading,
        openDialog,
        setOpenDialog,

        handleFilters,
        filters,

        leadForm,
        catalogs,

        openCreateLead,
        openEditLead,
        handleEditById,

        updateForm,
        saveLead
    };
}
